/**
 * ReorderClipsModal — the old "drag your clips into order" tool, now inside the Social Library.
 * Loads the clips behind an order's Sneak Peek, lets the agent drag / arrow them (or drop a clip),
 * then re-renders the Sneak Peek straight back into the library.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, GripVertical, ChevronUp, ChevronDown, Loader2, Film, Ban, RotateCcw, Check, AlertTriangle, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_COPY = {
  preparing: "Preparing clips — matching colours",
  submitting: "Sending to the renderer",
  planned: "Queued at the renderer",
  waiting: "Queued at the renderer",
  transcribing: "Rendering",
  rendering: "Rendering your Sneak Peek",
  succeeded: "Done",
  failed: "Render failed",
};

export default function ReorderClipsModal({ jobId, title, onClose, onStarted }) {
  const [data, setData] = useState(null);
  const [clips, setClips] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [job, setJob] = useState(null);
  const dragIdx = useRef(null);
  const [dragging, setDragging] = useState(null);

  useEffect(() => {
    let cancelled = false, tries = 0, timer = null;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/reels/reorder`, { params: { wms_job_id: jobId } });
        if (cancelled) return;
        setData((prev) => prev || r.data);
        // keep the user's order/exclusions; only fill in thumbnails as they arrive
        setClips((prev) => prev.length ? prev.map((c) => ({ ...c, thumb_url: c.thumb_url || (r.data.clips.find((n) => n.public_id === c.public_id) || {}).thumb_url || "" })) : (r.data.clips || []));
        if (r.data.thumbs_pending > 0 && tries++ < 12) timer = setTimeout(load, 3000);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Couldn't load the clips for this order");
      }
    };
    load();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [jobId]);

  useEffect(() => {
    if (!job || (job.done && !job.library_pending)) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/reels/status/${job.batch_id}`);
        setJob((prev) => ({ ...r.data, started_at: prev?.started_at, version: prev?.version }));
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [job]);

  const originalOrder = (data?.clips || []).map((c) => c.public_id);
  const currentOrder = clips.map((c) => c.public_id);
  const changed = excluded.size > 0 || currentOrder.some((id, i) => id !== originalOrder[i]);
  const kept = clips.filter((c) => !excluded.has(c.public_id)).length;

  const move = (from, to) => {
    if (to < 0 || to >= clips.length || from === to) return;
    setClips((prev) => { const next = [...prev]; const [item] = next.splice(from, 1); next.splice(to, 0, item); return next; });
  };
  const toggle = (id) => setExcluded((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const reset = () => { setClips(data.clips); setExcluded(new Set()); };

  const rerender = async () => {
    if (!changed || kept < 2 || submitting) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/reels/reorder/${data.source_id}`, { order: currentOrder, excluded: [...excluded] });
      const started_at = Date.now();
      setJob({ ...r.data.jobs?.[0], batch_id: r.data.batch_id, version: r.data.version, done: false, in_library: false, library_pending: false, started_at });
      onStarted?.({ batch_id: r.data.batch_id, group_key: `job:${jobId}`, title: r.data.title || title, version: r.data.version, started_at });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the re-render");
    } finally {
      setSubmitting(false);
    }
  };

  // Live reorder while dragging: the list opens a gap where the clip will land instead of asking you to drop onto another card.
  const onDragStart = (e, idx) => { dragIdx.current = idx; setDragging(idx); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", String(idx)); } catch { /* older browsers */ } };
  const onDragEnter = (idx) => {
    if (dragIdx.current === null || dragIdx.current === idx) return;
    move(dragIdx.current, idx);
    dragIdx.current = idx;
    setDragging(idx);
  };
  const onDragEnd = () => { dragIdx.current = null; setDragging(null); };

  return (
    <div className="smp-modal-overlay" onClick={onClose} data-testid="smp-reorder-modal">
      <div className="smp-modal smp-ro" onClick={(e) => e.stopPropagation()}>
        <button className="smp-modal-close" onClick={onClose} data-testid="smp-reorder-close"><X size={18} /></button>
        <header className="smp-ro-head">
          <h3>Reorder clips</h3>
          <p>{title}{data?.version ? ` · Sneak Peek v${data.version}` : ""}{data?.music_name ? ` · ♪ ${data.music_name}` : ""}</p>
          <small>Drag the clips (or use the arrows) into the order you want, switch any off, then re-render. Cuts, music, template and logo stay exactly as the editor set them.</small>
        </header>

        {error ? (
          <div className="smp-ro-empty" data-testid="smp-reorder-error"><AlertTriangle size={18} /> {error}</div>
        ) : !data ? (
          <div className="smp-ro-empty"><Loader2 size={18} className="smp-spin" /> Loading clips…</div>
        ) : job ? (
          <ReorderProgress job={job} onClose={onClose} />
        ) : (
          <>
            <div className="smp-ro-list" data-testid="smp-reorder-list">
              {clips.map((clip, idx) => {
                const off = excluded.has(clip.public_id);
                return (
                  <div
                    key={clip.public_id}
                    className={`smp-ro-card ${dragging === idx ? "is-dragging" : ""} ${off ? "is-off" : ""}`}
                    draggable
                    onDragStart={(e) => onDragStart(e, idx)}
                    onDragEnter={() => onDragEnter(idx)}
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
                    onDrop={(e) => { e.preventDefault(); onDragEnd(); }}
                    onDragEnd={onDragEnd}
                    data-testid={`smp-reorder-card-${idx}`}
                  >
                    <span className="smp-ro-index">{off ? "—" : clips.slice(0, idx).filter((c) => !excluded.has(c.public_id)).length + 1}</span>
                    <span className="smp-ro-grip"><GripVertical size={16} /></span>
                    <div className="smp-ro-thumb">
                      {clip.thumb_url ? <img src={clip.thumb_url} alt="" loading="lazy" onError={(e) => e.currentTarget.remove()} /> : <Loader2 size={16} className="smp-spin" />}
                    </div>
                    <div className="smp-ro-meta">
                      <span className="smp-ro-name" title={clip.name}>{clip.name}</span>
                      <span className="smp-ro-sub">{clip.start_time != null && clip.end_time != null ? `${(clip.end_time - clip.start_time).toFixed(1)}s used` : ""}{clip.duration ? ` of ${Math.round(clip.duration)}s` : ""}{off ? " · off" : ""}</span>
                    </div>
                    <div className="smp-ro-actions">
                      <button onClick={() => toggle(clip.public_id)} title={off ? "Put back in" : "Leave out"} data-testid={`smp-reorder-toggle-${idx}`}>{off ? <RotateCcw size={14} /> : <Ban size={14} />}</button>
                      <button onClick={() => move(idx, idx - 1)} disabled={idx === 0} aria-label="Move up" data-testid={`smp-reorder-up-${idx}`}><ChevronUp size={15} /></button>
                      <button onClick={() => move(idx, idx + 1)} disabled={idx === clips.length - 1} aria-label="Move down" data-testid={`smp-reorder-down-${idx}`}><ChevronDown size={15} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
            <footer className="smp-ro-foot">
              <span className="smp-ro-count">{kept} of {clips.length} clips in the edit{changed ? " · order changed" : ""}</span>
              {changed && <button className="smp-ro-ghost" onClick={reset} data-testid="smp-reorder-reset"><RotateCcw size={14} /> Reset</button>}
              <button className="smp-download-btn smp-ro-render" onClick={rerender} disabled={!changed || kept < 2 || submitting} data-testid="smp-reorder-render">
                {submitting ? <Loader2 size={16} className="smp-spin" /> : <Sparkles size={16} />} Re-render Sneak Peek
              </button>
            </footer>
          </>
        )}
      </div>
    </div>
  );
}

export function useTicker(active) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [active]);
}

export const elapsedLabel = (startedAt) => {
  const s = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

const STEPS = [
  { key: "preparing", label: "Preparing clips", match: ["preparing"] },
  { key: "submitting", label: "Sent to renderer", match: ["submitting", "planned", "waiting"] },
  { key: "rendering", label: "Rendering", match: ["transcribing", "rendering"] },
  { key: "done", label: "In your Social Library", match: ["succeeded"] },
];

function ReorderProgress({ job, onClose }) {
  const failed = job.status === "failed";
  const done = job.status === "succeeded";
  useTicker(!done && !failed);
  const stepIdx = Math.max(0, STEPS.findIndex((s) => s.match.includes(job.status)));
  const activeIdx = done && !job.in_library ? 3 : stepIdx;
  return (
    <div className="smp-ro-progress" data-testid="smp-reorder-progress">
      <div className="smp-ro-progress-video">
        {done && job.url ? <video src={job.url} poster={job.snapshot_url || undefined} controls playsInline data-testid="smp-reorder-video" /> : (
          <div className="smp-ro-wait">{failed ? <AlertTriangle size={24} /> : <Loader2 size={24} className="smp-spin" />}<span>{STATUS_COPY[job.status] || job.status}…</span>{!failed && job.started_at && <b data-testid="smp-reorder-elapsed">{elapsedLabel(job.started_at)}</b>}</div>
        )}
      </div>
      <div className="smp-ro-progress-text">
        <strong data-testid="smp-reorder-status">{failed ? "Something went wrong" : done ? `New Sneak Peek${job.version ? ` v${job.version}` : ""} ready` : `Rendering Sneak Peek${job.version ? ` v${job.version}` : ""}`}</strong>
        {!failed && (
          <ol className="smp-ro-steps" data-testid="smp-reorder-steps">
            {STEPS.map((s, i) => (
              <li key={s.key} className={i < activeIdx || (done && job.in_library) ? "is-done" : i === activeIdx ? "is-active" : ""}>
                <span className="smp-ro-step-dot">{i < activeIdx || (done && job.in_library) ? <Check size={11} /> : i === activeIdx ? <Loader2 size={11} className="smp-spin" /> : null}</span>{s.label}
              </li>
            ))}
          </ol>
        )}
        {failed && <small>{job.error || "The renderer rejected this one — try again in a minute."}</small>}
        {!failed && !done && <small>Takes about <strong>2 minutes</strong>. You can close this — we'll keep tracking it in your library and let you know when it lands.</small>}
        <button className="smp-ro-ghost" onClick={onClose} data-testid="smp-reorder-done">{done ? "Close" : "Close — keep rendering"}</button>
      </div>
    </div>
  );
}
